import type { CSSProperties, ReactElement } from "react";
import { Cbutton } from "@/components/ui/Cbutton";
import { ButtonPreset } from "@/constants/componentPresets";
import { getFontFamily } from "@/theme/typography";
import { CARD_RADIUS, CATEGORY_PILL_HEIGHT } from "./chatteaser3.constants";
interface CategoryProps {
    tabs: readonly string[];
    activeTab: number;
    onSelect: (index: number) => void;
    // Signed -1..1 progress toward the next/previous tab while scrolling over the rail.
    scrollProgress: number;
    // Intro: 0 = reeled fully to the bottom, 1 = settled in place.
    railReel?: number;
    // Intro: 0..1 fade for the mint pill.
    railPillOpacity?: number;
    // Intro: 0 = circle, 1 = full pill width.
    railPillExpand?: number;
    // Cylinder tweaks (per unit distance from centre):
    rotation: number; // deg rotateX
    depth: number; // px translateZ recede
    spread: number; // px vertical spacing
}
// Locked-in active surface (Mint glass).
const MINT_PILL: CSSProperties = {
    backgroundImage: "linear-gradient(180deg, rgba(95,243,184,0.12) 0%, rgba(95,243,184,0.02) 55%, rgba(95,243,184,0.06) 100%)",
    border: "1px solid rgba(95,243,184,0.26)",
    boxShadow: "inset 0 1.5px 0.5px rgba(255,255,255,0.3), inset 0 -16px 26px -14px rgba(95,243,184,0.22), 0 20px 44px -26px rgba(0,0,0,0.7)",
};
const ACTIVE_COLOR = "rgba(255,255,255,0.98)";
// How many items below the bottom the wheel starts at, on enter (fully scrolled
// to the bottom, then reels up to centre).
const REEL_ITEMS = 7;
const RAIL_PERSPECTIVE = 1200;
const PILL_WIDTH_FRACTION = 0.36 * 0.86;
const DEPTH_REST_TRANSITION = "transform 0.34s cubic-bezier(0.22,1,0.36,1), opacity 0.34s ease, color 0.25s ease";
const RAIL_AREA_STYLE: CSSProperties = {
    position: "absolute",
    top: 0,
    bottom: 0,
    right: 0,
    width: "36%",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    perspective: RAIL_PERSPECTIVE,
};
const BARE_BUTTON: CSSProperties = {
    fontFamily: getFontFamily(),
};
function getPillStyle(opacity: number, expand: number): CSSProperties {
    const base: CSSProperties = {
        position: "absolute",
        top: "50%",
        height: CATEGORY_PILL_HEIGHT,
        marginTop: -CATEGORY_PILL_HEIGHT / 2,
        borderRadius: CARD_RADIUS,
        pointerEvents: "none",
        opacity,
        ...MINT_PILL,
    };
    if (expand >= 1) {
        return {
            ...base,
            left: "7%",
            right: "7%",
            transition: "opacity 0.3s ease",
        };
    }
    const viewport = typeof window === "undefined" ? 1280 : window.innerWidth;
    const fullWidth = viewport * PILL_WIDTH_FRACTION;
    const width = CATEGORY_PILL_HEIGHT + (fullWidth - CATEGORY_PILL_HEIGHT) * expand;
    return { ...base, left: "50%", width, transform: "translateX(-50%)" };
}
export function DepthCarouselRail({ tabs, activeTab, onSelect, scrollProgress, railReel = 1, railPillOpacity = 1, railPillExpand = 1, rotation, depth, spread, }: CategoryProps): ReactElement {
    const isScrolling = scrollProgress !== 0;
    const isIntro = railReel < 1;
    const reelItems = (1 - railReel) * REEL_ITEMS;
    return (
      <div data-el="Cchatteaser3/DepthCarouselRail/Root" style={RAIL_AREA_STYLE}>
        <div data-el="Cchatteaser3/DepthCarouselRail/Status" style={getPillStyle(railPillOpacity, railPillExpand)}/>
        {tabs.map((tab, index) => {
            const distance = index - activeTab - scrollProgress;
            const distanceAbs = Math.abs(distance);
            const isCentered = distanceAbs < 0.5;
            // Rotation + depth are a function of distance (the cylinder); the reel only
            // translates the formed wheel up from the bottom.
            const translateY = (distance + reelItems) * spread;
            const translateZ = -(distanceAbs * distanceAbs) * depth;
            const rotateX = -distance * rotation;
            const opacity = Math.max(0.16, 1 - distanceAbs * 0.32);
            return (<Cbutton rootAttributes={{ "data-el": "Cchatteaser3/DepthCarouselRail/Entry" }} key={tab} preset={ButtonPreset.Ghost} ariaLabel={tab} onClick={() => onSelect(index)} padding={0} radius={CARD_RADIUS} disableClickMotion disableHoverEffects style={{
                    position: "absolute",
                    top: "50%",
                    left: 0,
                    right: 0,
                    marginTop: -32,
                    height: 64,
                    cursor: "pointer",
                    transform: `translateY(${translateY}px) translateZ(${translateZ}px) rotateX(${rotateX}deg)`,
                    backfaceVisibility: "hidden",
                    opacity,
                    transition: isScrolling || isIntro ? "none" : DEPTH_REST_TRANSITION,
                }} containerStyle={{
                    ...BARE_BUTTON,
                    width: "100%",
                    height: "100%",
                    fontSize: 24,
                    fontWeight: isCentered ? 600 : 400,
                    color: isCentered ? ACTIVE_COLOR : "rgba(255,255,255,0.55)",
                    whiteSpace: "nowrap",
                }}>
              {tab}
            </Cbutton>);
        })}
      </div>
    );
}
